'use client';

import type { DeepKeys } from '@tanstack/react-form';

import { Button } from '@connected/ui/components/button';
import { Pagination } from '@connected/ui/components/pagination';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@connected/ui/components/table';
import { cn } from '@connected/ui/lib/utils';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import type {
    AnyObjectOfArrayInObject,
    FormTableColumn,
    FormTableExpansionConfig,
    FormTableProps,
} from './types';

/**
 * Calculate sticky column offsets for left and right sticky columns.
 */
function calculateStickyOffsets<TData extends Record<string, unknown>>(
    columns: FormTableColumn<TData>[],
): Map<keyof TData, { offset: number; side: 'left' | 'right' }> {
    const stickyInfo = new Map<keyof TData, { offset: number; side: 'left' | 'right' }>();

    // Find all sticky columns and their positions
    const stickyColumns = columns
        .map((col, index) => ({ col, index }))
        .filter(({ col }) => col.sticky);

    if (stickyColumns.length === 0)
        return stickyInfo;

    // Determine midpoint to decide left vs right sticky
    const midpoint = Math.floor(columns.length / 2);

    // Calculate left sticky offsets (from start)
    let leftOffset = 0;
    for (const { col, index } of stickyColumns) {
        if (index <= midpoint) {
            stickyInfo.set(col.accessorKey, { offset: leftOffset, side: 'left' });
            // Parse size to get pixel value, default to 150px
            const width = col.size ? Number.parseInt(col.size, 10) || 150 : 150;
            leftOffset += width;
        }
    }

    // Calculate right sticky offsets (from end)
    let rightOffset = 0;
    for (let i = stickyColumns.length - 1; i >= 0; i--) {
        const { col, index } = stickyColumns[i];
        if (index > midpoint) {
            stickyInfo.set(col.accessorKey, { offset: rightOffset, side: 'right' });
            const width = col.size ? Number.parseInt(col.size, 10) || 150 : 150;
            rightOffset += width;
        }
    }

    return stickyInfo;
}

/**
 * Default row key accessor - uses 'id' field or falls back to index.
 */
function defaultGetRowKey<TData extends AnyObjectOfArrayInObject, TFieldArrayName extends DeepKeys<TData>>(row: TData[TFieldArrayName][number], index: number): string {
    return (row as Record<string, unknown>).id as string ?? String(index);
}

/**
 * FormTable - A table component that integrates with TanStack Form field arrays.
 *
 * Renders a table where each cell is connected to a form field, enabling
 * form-aware editing within the table structure.
 */
export function FormTable<TData extends AnyObjectOfArrayInObject, TFieldArrayName extends DeepKeys<TData>>({
    form,
    fieldArrayName,
    columns,
    emptyMessage = 'No data available',
    pageSize = 10,
    currentPage: externalCurrentPage,
    onPageChange,
    className,
    getRowKey = defaultGetRowKey,
    expansion,
    controlsColumn,
}: FormTableProps<TData, TFieldArrayName>) {
    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    const currentPage = externalCurrentPage ?? internalCurrentPage;

    // Expansion state management
    const [expandedRows, setExpandedRows] = useState<Set<string>>(
        () => new Set(expansion?.defaultExpanded ?? []),
    );

    const handlePageChange = (page: number) => {
        if (onPageChange) {
            onPageChange(page);
        }
        else {
            setInternalCurrentPage(page);
        }
    };

    /**
     * Toggle expansion state for a row.
     */
    const handleToggleExpand = useCallback((rowKey: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            const isCurrentlyExpanded = next.has(rowKey);
            const collapsible = expansion?.collapsible ?? true;

            if (expansion?.type === 'single') {
                // Single mode: only one row expanded at a time
                if (isCurrentlyExpanded) {
                    if (collapsible) {
                        next.delete(rowKey);
                    }
                    // If not collapsible, do nothing (keep it expanded)
                }
                else {
                    next.clear();
                    next.add(rowKey);
                }
            }
            else {
                // Multiple mode: toggle independently
                if (isCurrentlyExpanded) {
                    if (collapsible) {
                        next.delete(rowKey);
                    }
                }
                else {
                    next.add(rowKey);
                }
            }

            return next;
        });
    }, [expansion?.type, expansion?.collapsible]);

    // Filter visible columns
    const visibleColumns = useMemo(
        () => columns.filter(col => col.visible !== false),
        [columns],
    );

    // Check if we should show the controls column
    const showControlsColumn = Boolean(controlsColumn?.showExpand && expansion);

    // Generate column widths for colgroup (including controls column if present)
    const columnWidths = useMemo(() => {
        const widths = visibleColumns.map(col => col.size);
        if (showControlsColumn) {
            widths.unshift(controlsColumn?.width ?? '48px');
        }
        return widths;
    }, [visibleColumns, showControlsColumn, controlsColumn?.width]);

    const columnMinWidths = useMemo(() => {
        const minWidths = visibleColumns.map(col => col.minSize);
        if (showControlsColumn) {
            minWidths.unshift(controlsColumn?.width ?? '48px');
        }
        return minWidths;
    }, [visibleColumns, showControlsColumn, controlsColumn?.width]);

    // Total column count including controls column
    const totalColumnCount = visibleColumns.length + (showControlsColumn ? 1 : 0);

    // Calculate sticky column offsets
    const stickyOffsets = useMemo(
        () => calculateStickyOffsets(visibleColumns),
        [visibleColumns],
    );

    // Calculate total width of right-sticky columns for scroll adjustment
    const rightStickyWidth = useMemo(() => {
        let totalWidth = 0;
        for (const [, data] of stickyOffsets) {
            if (data.side === 'right') {
                // Find the column to get its width
                const col = visibleColumns.find(c => stickyOffsets.get(c.accessorKey) === data);
                const width = col?.size ? Number.parseInt(col.size, 10) || 150 : 150;
                totalWidth += width;
            }
        }
        return totalWidth;
    }, [stickyOffsets, visibleColumns]);

    // Ref for the scrollable container
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Handle focus events to scroll focused elements into view, accounting for sticky columns
    const handleFocusWithinTable = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        const container = scrollContainerRef.current;
        const target = event.target as HTMLElement;

        if (!container || !target)
            return;

        // Only handle focusable elements (inputs, selects, buttons, etc.)
        const focusableElements = ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'];
        if (!focusableElements.includes(target.tagName) && !target.hasAttribute('tabindex'))
            return;

        // Get the cell containing the focused element
        const cell = target.closest('td');
        if (!cell)
            return;

        // Use requestAnimationFrame to run after browser's default scroll-into-view
        requestAnimationFrame(() => {
            const containerRect = container.getBoundingClientRect();
            const cellRect = cell.getBoundingClientRect();

            // Calculate visible area accounting for right sticky column
            const visibleRightEdge = containerRect.right - rightStickyWidth;

            // Check if the cell is obscured by the right sticky column
            if (cellRect.right > visibleRightEdge) {
                // Calculate how much we need to scroll to bring the cell into view
                const scrollAmount = cellRect.right - visibleRightEdge + 16; // 16px padding
                container.scrollLeft += scrollAmount;
            }
        });
    }, [rightStickyWidth]);

    const getPageNumbers = (totalPages: number) => {
        const pages: (number | 'ellipsis')[] = [];
        const maxVisible = 7;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        }
        else {
            if (currentPage <= 4) {
                for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                }
                pages.push('ellipsis');
                pages.push(totalPages);
            }
            else if (currentPage >= totalPages - 3) {
                pages.push(1);
                pages.push('ellipsis');
                for (let i = totalPages - 4; i <= totalPages; i++) {
                    pages.push(i);
                }
            }
            else {
                pages.push(1);
                pages.push('ellipsis');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('ellipsis');
                pages.push(totalPages);
            }
        }

        return pages;
    };

    // Use form.Field with mode="array" for idiomatic TanStack Form array handling
    // This only re-renders when the array structure changes (add/remove), not on cell edits
    return (
        <form.Field name={fieldArrayName} mode="array">
            {(arrayField) => {
                const arrayValue = arrayField.state.value as TData[TFieldArrayName] | undefined;
                const arrayLength: number = arrayValue?.length ?? 0;
                const totalPages = Math.ceil(arrayLength / pageSize);
                const startIndex = (currentPage - 1) * pageSize;
                const paginatedCount = Math.min(pageSize, Math.max(0, arrayLength - startIndex));

                if (arrayLength === 0) {
                    return (
                        <div className="text-muted-foreground py-8 text-center">
                            {emptyMessage}
                        </div>
                    );
                }

                return (
                    <div className={cn('rounded-2xl border overflow-hidden', className)}>
                        <div
                            ref={scrollContainerRef}
                            className="overflow-x-auto"
                            onFocus={handleFocusWithinTable}
                        >
                            <Table>
                                {(columnWidths.some(Boolean) || columnMinWidths.some(Boolean)) && (
                                    <colgroup>
                                        {columnWidths.map((width, index) => (
                                            <col
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`col-${index}`}
                                                style={{
                                                    width: width ?? undefined,
                                                    minWidth: columnMinWidths[index] ?? undefined,
                                                }}
                                            />
                                        ))}
                                    </colgroup>
                                )}
                                <TableHeader className="bg-background-secondary">
                                    <TableRow>
                                        {/* Controls column header */}
                                        {showControlsColumn && (
                                            <TableHead className="w-12 px-2">
                                                {controlsColumn?.header ?? ''}
                                            </TableHead>
                                        )}
                                        {visibleColumns.map((column) => {
                                            const stickyData = stickyOffsets.get(column.accessorKey);
                                            const isLeftSticky = stickyData?.side === 'left';
                                            const isRightSticky = stickyData?.side === 'right';

                                            return (
                                                <TableHead
                                                    key={String(column.accessorKey)}
                                                    className={cn(
                                                        'px-4',
                                                        stickyData && 'sticky z-10 bg-background-secondary',
                                                        isLeftSticky && 'after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border',
                                                        isRightSticky && 'before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-border',
                                                    )}
                                                    style={{
                                                        left: isLeftSticky ? stickyData.offset : undefined,
                                                        right: isRightSticky ? stickyData.offset : undefined,
                                                    }}
                                                >
                                                    {column.header}
                                                </TableHead>
                                            );
                                        })}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {/* Map over indices, not data - each row subscribes to its own data via form.Field */}
                                    {Array.from({ length: paginatedCount }).map((_, paginatedIndex) => {
                                        const actualIndex = startIndex + paginatedIndex;
                                        return (
                                            <form.Field
                                                key={actualIndex}
                                                name={`${fieldArrayName}[${actualIndex}]` as typeof fieldArrayName}
                                            >
                                                {(rowField) => {
                                                    const row = rowField.state.value as TData[TFieldArrayName][number];
                                                    const rowKey = getRowKey(row, actualIndex);
                                                    const isExpanded = expandedRows.has(rowKey);

                                                    return (
                                                        <FormTableRowWithExpansion
                                                            key={rowKey}
                                                            form={form}
                                                            fieldArrayName={fieldArrayName}
                                                            row={row}
                                                            rowIndex={actualIndex}
                                                            rowKey={rowKey}
                                                            columns={visibleColumns}
                                                            stickyOffsets={stickyOffsets}
                                                            showControlsColumn={showControlsColumn}
                                                            isExpanded={isExpanded}
                                                            onToggleExpand={handleToggleExpand}
                                                            expansion={expansion}
                                                            totalColumnCount={totalColumnCount}
                                                        />
                                                    );
                                                }}
                                            </form.Field>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>

                        {totalPages > 1 && (
                            <div className="border-t p-4">
                                <Pagination>
                                    <Pagination.Content>
                                        <Pagination.Item>
                                            <Pagination.Previous
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (currentPage > 1)
                                                        handlePageChange(currentPage - 1);
                                                }}
                                                className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                                            />
                                        </Pagination.Item>
                                        {getPageNumbers(totalPages).map((page, index) => (
                                            <Pagination.Item key={page === 'ellipsis' ? `ellipsis-${index}` : `page-${page}`}>
                                                {page === 'ellipsis'
                                                    ? (
                                                            <Pagination.Ellipsis />
                                                        )
                                                    : (
                                                            <Pagination.Link
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handlePageChange(page);
                                                                }}
                                                                isActive={currentPage === page}
                                                            >
                                                                {page}
                                                            </Pagination.Link>
                                                        )}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Item>
                                            <Pagination.Next
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (currentPage < totalPages)
                                                        handlePageChange(currentPage + 1);
                                                }}
                                                className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                                            />
                                        </Pagination.Item>
                                    </Pagination.Content>
                                </Pagination>
                            </div>
                        )}
                    </div>
                );
            }}
        </form.Field>
    );
}

/**
 * Internal component for rendering a single row with form fields and optional expansion.
 */
function FormTableRowWithExpansion<TFormData extends AnyObjectOfArrayInObject, TFieldArrayName extends DeepKeys<TFormData>>({
    form,
    fieldArrayName,
    row,
    rowIndex,
    rowKey,
    columns,
    stickyOffsets,
    showControlsColumn,
    isExpanded,
    onToggleExpand,
    expansion,
    totalColumnCount,
}: {
    form: FormTableProps<TFormData, TFieldArrayName>['form'];
    fieldArrayName: TFieldArrayName;
    row: TFormData[TFieldArrayName][number];
    rowIndex: number;
    rowKey: string;
    columns: FormTableColumn<TFormData[TFieldArrayName][number]>[];
    stickyOffsets: Map<keyof TFormData[TFieldArrayName][number], { offset: number; side: 'left' | 'right' }>;
    showControlsColumn: boolean | undefined;
    isExpanded: boolean;
    onToggleExpand: (rowKey: string) => void;
    expansion: FormTableExpansionConfig<TFormData[TFieldArrayName][number]> | undefined;
    totalColumnCount: number;
}) {
    const fieldNamePrefix = `${fieldArrayName}[${rowIndex}]` as const;

    return (
        <>
            <TableRow className={cn(isExpanded && 'border-b-0')}>
                {/* Controls column with expand button */}
                {showControlsColumn && (
                    <TableCell className="w-12 px-2 py-3">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => onToggleExpand(rowKey)}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                        >
                            {isExpanded
                                ? <ChevronDown className="h-4 w-4" />
                                : <ChevronRight className="h-4 w-4" />}
                        </Button>
                    </TableCell>
                )}
                {columns.map((column) => {
                    const fieldName = `${fieldNamePrefix}.${String(column.accessorKey)}` as const;
                    const stickyData = stickyOffsets.get(column.accessorKey);
                    const isLeftSticky = stickyData?.side === 'left';
                    const isRightSticky = stickyData?.side === 'right';

                    return (
                        <TableCell
                            key={String(column.accessorKey)}
                            className={cn(
                                'px-4 py-3',
                                stickyData && 'sticky z-10 bg-background-primary',
                                isLeftSticky && 'after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border',
                                isRightSticky && 'before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-border',
                            )}
                            style={{
                                left: isLeftSticky ? stickyData.offset : undefined,
                                right: isRightSticky ? stickyData.offset : undefined,
                            }}
                        >
                            {column.cell({
                                form,
                                fieldName,
                                row,
                                rowIndex,
                            })}
                        </TableCell>
                    );
                })}
            </TableRow>
            {/* Expanded content row */}
            {isExpanded && expansion && (
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableCell colSpan={totalColumnCount} className="p-0">
                        {expansion.renderExpanded({
                            form,
                            row,
                            rowIndex,
                            fieldNamePrefix,
                        })}
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}
